import React, { useContext, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PropTypes, { bool, func } from 'prop-types';
import { AuthContext } from 'authentication/AuthContext';
import * as R from 'ramda';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { Chip } from '@mui/material';
import { isValid, startOfToday } from 'date-fns';
import {
  useGetAffiliateProductsQuery,
  useGetProductOriginsQuery,
  useGetReidVaporPressureQuery,
} from '../../reconsignmentsEndpoints';
import { CONSIGNMENT_STATUS_OPTIONS, INVENTORY_STATUS_OPTIONS } from '../../utils/constant';

const ConfigurationStep = ({
  bulk,
  handleConsignmentStatusChange,
  handleInventoryStatusChange,
  handleTransferDateChange,
  handleLocationSelect,
  errors,
  isDrawerOpen,
  setIsDrawerOpen,
  reconsignmentState,
  setReconsignmentState,
  validateField,
  setSelectedOrigin,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { affiliates } = useContext(AuthContext);

  const isDateInvalidOrPast = date => !isValid(date) || date < startOfToday();

  const [currentDate, setCurrentDate] = useState(startOfToday());
  if (isDateInvalidOrPast(currentDate)) {
    setCurrentDate(new Date());
  }

  useEffect(() => {
    if (!R.isEmpty(affiliates) && R.isNil(reconsignmentState.inventoryOwner)) {
      setReconsignmentState(R.assoc('inventoryOwner', affiliates[0]));
    }
  }, [affiliates, reconsignmentState.inventoryOwner, setReconsignmentState]);

  const productsParams = {
    inventoryOwner: {
      companyCustomerIdName: R.path(['inventoryOwner', 'customerIdName'], reconsignmentState),
      dataCompanyId: R.path(['inventoryOwner', 'dataCompanyId'], reconsignmentState),
    },
  };
  const {
    data: productsData = [],
    isSuccess: isSuccessProducts,
    isError: isErrorProducts,
    isFetching: isFetchingProducts,
  } = useGetAffiliateProductsQuery(
    {
      params: {
        inventoryOwner: productsParams.inventoryOwner,
      },
    },
    { skip: !reconsignmentState.inventoryOwner }
  );

  const handleProductChange = (_, value) => {
    setReconsignmentState(prev =>
      R.pipe(
        R.assoc('origin', []),
        R.assoc('product', value),
        R.assoc('rvp', value && value.isGasoline ? 'Any' : null)
      )(prev)
    );
    handleLocationSelect(null);
    validateField('product', value);
  };

  const selectedProduct = R.prop('product', reconsignmentState);
  const isGasoline = selectedProduct && R.propEq('isGasoline', true, selectedProduct);

  const rvpParams = {
    ...productsParams,
    productId: R.path(['product', 'productId'], reconsignmentState),
    // only include RVP when it's not the default "Any"
    ...(isGasoline && reconsignmentState.rvp !== 'Any'
      ? { RVP: Number(reconsignmentState.rvp) }
      : {}),
  };

  const { data: rvpData = [] } = useGetReidVaporPressureQuery(
    { params: rvpParams },
    { skip: !isGasoline }
  );
  const rvps = rvpData.length > 0 ? R.prepend('Any', rvpData) : ['Any'];

  const originsParams = {
    ProductId: R.path(['product', 'productId'], reconsignmentState),
    inventoryOwner: {
      companyCustomerIdName: R.path(['inventoryOwner', 'customerIdName'], reconsignmentState),
      dataCompanyId: R.path(['inventoryOwner', 'dataCompanyId'], reconsignmentState),
    },
    // only include RVP when not "Any" (case‑insensitive)
    ...(reconsignmentState.rvp && R.toUpper(String(reconsignmentState.rvp)) !== 'ANY'
      ? { RVP: Number(reconsignmentState.rvp) }
      : {}),

    // only include ConsignmentStatus when not "All" (case‑insensitive)
    ...(R.prop('consignmentStatus', reconsignmentState) &&
    R.toUpper(String(R.prop('consignmentStatus', reconsignmentState))) !== 'ALL'
      ? { ConsignmentStatus: R.prop('consignmentStatus', reconsignmentState) }
      : {}),

    // only include InventoryStatus when not "All" (case‑insensitive)
    ...(R.prop('inventoryStatus', reconsignmentState) &&
    R.toUpper(String(R.prop('inventoryStatus', reconsignmentState))) !== 'ALL'
      ? { InventoryStatus: R.prop('inventoryStatus', reconsignmentState) }
      : {}),
  };

  const { data: originsData = [], isError: isErrorOrigins } = useGetProductOriginsQuery(
    { params: originsParams },
    { skip: !reconsignmentState.product }
  );

  const displayedOrigins = isErrorOrigins ? [] : originsData;

  const handleInventoryOwnerChange = (_, value) => {
    setReconsignmentState(prev =>
      R.pipe(
        R.assoc('inventoryOwner', value),
        R.assoc('origin', []),
        R.assoc('product', null),
        R.assoc('rvp', null)
      )(prev)
    );
    handleLocationSelect(null);
  };

  const handleOriginChange = (_, values) => {
    setSelectedOrigin(values);
    setReconsignmentState(prev => ({
      ...prev,
      origin: values || [],
    }));
    handleLocationSelect(null);
  };

  const handleRVPChange = e => {
    const newRVP = e.target.value;

    setReconsignmentState(prev =>
      R.pipe(R.assoc('rvp', newRVP), R.assoc('origin', newRVP !== 'Any' ? [] : prev.origin))(prev)
    );
    handleLocationSelect(null);
    validateField('rvp', newRVP);
  };

  const renderTags = (tags, getTagProps) =>
    tags.map((item, index) => (
      <Chip
        label={item.originName}
        sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
        variant="outlined"
        {...getTagProps({ index })}
      />
    ));

  return (
    <Drawer
      anchor={lgUp ? 'left' : 'right'}
      onClose={() => setIsDrawerOpen(false)}
      open={isDrawerOpen}
      sx={{
        '& .MuiDrawer-paper': {
          '&::-webkit-scrollbar': {
            display: 'none',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          },
          boxSizing: 'border-box',
          mt: '2.5',
          pb: 9,
          pt: 5,
          width: { lg: 395, xs: '100%' },
        },
        backgroundColor: 'surface.white',
        borderRight: '1px solid',
        borderRightColor: 'graphic.light',
        left: 0,
        overflowY: 'auto',
        // Actually use the bulk prop to modify position
        position: { lg: bulk ? 'absolute' : 'fixed', xs: 'initial' },
        top: 48,
        width: { lg: 395, xs: '100%' },
        // Use the bulk prop to determine z-index
        zIndex: bulk ? 100 : 1000, // Lower z-index for bulk mode
      }}
      variant={lgUp ? 'permanent' : 'temporary'}
    >
      <Stack sx={{ px: 1, py: 3 }}>
        <Stack spacing={2} sx={{ pb: 2, px: 2 }}>
          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <Typography variant="h6">Configuration</Typography>
          </Stack>
          <Divider />
          <Typography sx={{ color: 'text.secondary' }} variant="body2">
            Please provide the following information for your reconsignment.
          </Typography>
          <DatePicker
            label="Transfer Date"
            minDate={currentDate}
            onChange={handleTransferDateChange}
            slotProps={{
              textField: {
                error: !!errors.transferDate,
                helperText: errors.transferDate,
              },
            }}
            value={reconsignmentState.transferDate || currentDate}
          />
          {affiliates && affiliates.length > 1 && (
            <Autocomplete
              disableClearable
              disabled={R.isEmpty(affiliates) || R.equals(R.length(affiliates), 1)}
              getOptionLabel={R.prop('customerNameShort')}
              isOptionEqualToValue={R.eqProps('customerIdName')}
              noOptionsText="No options"
              onChange={handleInventoryOwnerChange}
              options={affiliates}
              renderInput={params => (
                <TextField
                  {...params}
                  error={!!errors.inventoryOwner}
                  helperText={errors.inventoryOwner}
                  label="Inventory Owner"
                />
              )}
              value={reconsignmentState.inventoryOwner || null}
            />
          )}
          <Autocomplete
            disableClearable
            disabled={!(isSuccessProducts || isErrorProducts) || isFetchingProducts}
            getOptionLabel={R.prop('productName')}
            isOptionEqualToValue={R.eqProps('productId')}
            noOptionsText="No options"
            onChange={handleProductChange}
            options={productsData}
            renderInput={params => (
              <TextField
                {...params}
                error={!!errors.product}
                helperText={errors.product}
                label="Product"
              />
            )}
            value={reconsignmentState.product || null}
          />
          <FormControl disabled={!isGasoline} sx={{ flexBasis: 0, flexGrow: 1 }}>
            <InputLabel id="rvp">RVP</InputLabel>
            <Select
              label="RVP"
              labelId="rvp"
              onChange={handleRVPChange}
              sx={{
                '> .MuiSelect-icon': { right: 8 },
              }}
              value={reconsignmentState.rvp || ''}
            >
              {rvps && rvps.length > 0 ? (
                rvps.map(rvpOption => (
                  <MenuItem key={rvpOption} value={rvpOption}>
                    {rvpOption}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No options
                </MenuItem>
              )}
            </Select>
          </FormControl>
          <Autocomplete
            disableClearable
            disabled={!reconsignmentState.product}
            getOptionLabel={option => option?.originName || ''}
            isOptionEqualToValue={(option, value) => option.originId === value.originId}
            multiple
            noOptionsText="No options"
            onChange={handleOriginChange}
            options={displayedOrigins}
            renderInput={params => <TextField {...params} helperText="Optional" label="Origin" />}
            renderTags={renderTags}
            value={reconsignmentState.origin || []}
          />
          <Stack sx={{ pt: 2 }}>
            <FormControl>
              <FormLabel id="inventory-status">Inventory Status</FormLabel>
              <RadioGroup
                aria-labelledby="inventory-status"
                name="inventory-status-group"
                onChange={handleInventoryStatusChange}
                value={R.propOr('All', 'inventoryStatus', reconsignmentState)}
              >
                {INVENTORY_STATUS_OPTIONS.map(opt => (
                  <FormControlLabel
                    control={<Radio />}
                    key={opt.code}
                    label={opt.label}
                    sx={{ mb: -2 }}
                    value={opt.code}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Stack>
          <Stack sx={{ pt: 2 }}>
            <FormControl>
              <FormLabel id="consignment-status">Consignment Status</FormLabel>
              <RadioGroup
                aria-labelledby="consignment-status"
                name="consignment-status-group"
                onChange={handleConsignmentStatusChange}
                value={R.propOr('UNCONTROL', 'consignmentStatus', reconsignmentState)}
              >
                {CONSIGNMENT_STATUS_OPTIONS.map(opt => (
                  <FormControlLabel
                    control={<Radio />}
                    key={opt.code}
                    label={opt.label}
                    sx={{ mb: -2 }}
                    value={opt.code}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};

ConfigurationStep.propTypes = {
  bulk: bool,
  errors: PropTypes.shape({
    inventoryOwner: PropTypes.string,
    product: PropTypes.string,
    rvp: PropTypes.string,
    transferDate: PropTypes.string,
  }).isRequired,
  handleClearAll: func.isRequired,
  handleConsignmentStatusChange: func.isRequired,
  handleInventoryStatusChange: func.isRequired,
  handleLocationSelect: func.isRequired,
  handleTransferDateChange: func.isRequired,
  isDrawerOpen: bool.isRequired,
  reconsignmentState: PropTypes.shape({
    inventoryOwner: PropTypes.shape({
      companyCustomerIdName: PropTypes.string,
      customerIdName: PropTypes.string,
      dataCompanyId: PropTypes.string,
    }),
    locationId: PropTypes.number,
    origin: PropTypes.arrayOf(PropTypes.shape({})),
    product: PropTypes.shape({
      isGasoline: PropTypes.bool,
      productId: PropTypes.string,
      productName: PropTypes.string,
    }),
    rvp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    transferDate: PropTypes.instanceOf(Date),
  }).isRequired,
  setIsDrawerOpen: func.isRequired,
  setReconsignmentState: func.isRequired,
  setSelectedOrigin: func.isRequired,
  validateField: func.isRequired,
};

ConfigurationStep.defaultProps = {
  bulk: false,
};

export default ConfigurationStep;
